import { ReactElement } from 'react'
import { EditOutlined } from '@mui/icons-material'
import { useTheme } from '@mui/material'
import { ToggleButtonDropDown, ToggleDropDownOptions } from '@ui/actions'

export type IFCFileToolMode = 'settings' | 'alignment' | 'filter' | 'storey' | 'type'

/**
 * Tools that operate on the IFC model metadata (property_data) read asynchronously after upload.
 * They are disabled until that metadata is available, so the user cannot open a tool that would
 * write to a not-yet-computed field.
 */
const METADATA_DEPENDENT_MODES: IFCFileToolMode[] = ['filter', 'storey', 'type']

interface Props {
  /** The currently active option, or undefined when neither is open. */
  value?: IFCFileToolMode
  onSelect: (value: IFCFileToolMode) => void
  disabled?: boolean
  /** Whether the IFC model metadata has been computed yet; gates the metadata-dependent tools. */
  hasModelMetadata?: boolean
}

/**
 * Settings dropdown for the IFC Import toolbar (styled like the "Zeichnen" tool): opens a selection
 * between the "Datei" and "Ausrichtung" drawer panels.
 */
const SettingsToolSelect = ({
  value,
  onSelect,
  disabled = false,
  hasModelMetadata = false,
}: Props): ReactElement => {
  const theme = useTheme()

  const options = [
    { label: 'Datei', value: 'settings' },
    { label: 'Ausrichtung', value: 'alignment' },
    { label: 'Filtern', value: 'filter' },
    { label: 'Stockwerkszuweisung', value: 'storey' },
    { label: 'Typenzuweisungen', value: 'type' },
  ].map(option => ({
    ...option,
    disabled:
      !hasModelMetadata && METADATA_DEPENDENT_MODES.includes(option.value as IFCFileToolMode),
  }))

  return (
    <ToggleButtonDropDown
      hint="Bearbeiten"
      openAbove
      tooltipPlacement="top"
      toggleType="click"
      closeOnChildrenClick
      disabled={disabled}
      style={{
        color: theme.palette.grey[800],
        backgroundColor: value ? theme.palette.grey[400] : undefined,
        borderColor: theme.palette.grey[600],
      }}
      Icon={EditOutlined}
      data-cy="btn-ifc-settings"
    >
      <ToggleDropDownOptions
        value={value}
        onClick={next => onSelect(next as IFCFileToolMode)}
        options={options}
      />
    </ToggleButtonDropDown>
  )
}

export default SettingsToolSelect
